/**
 * Client helper for the list CSV export. The server endpoint answers either:
 *   - `200 text/csv` — small result: stream the file straight to a download.
 *   - `202 application/json {token}` — large result: the export runs async on
 *     the server; poll the status endpoint by token until a presigned URL is
 *     ready, then open it.
 * `runCsvExport` branches on that, delegating the side effects (download / toast
 * / poll) to injected handlers so it stays trivially testable.
 */

import { track } from "@/lib/analytics";

/** Minimal shape of the export-status poll response. */
export interface ExportStatus {
  status: string;
  url: string | null;
  token: string;
}

export interface CsvExportHandlers {
  /** Trigger a browser download of a CSV blob. */
  download: (blob: Blob, filename: string) => void;
  /** Called with the async token when the server queued the export (202). */
  onQueued: (token: string) => void;
  /** Poll the status endpoint until a presigned URL resolves (or null on failure). */
  poll: (token: string) => Promise<string | null>;
}

/** The lists whose CSV export goes through {@link runCsvExport}. */
type CsvExportType =
  | "custom_fields"
  | "forms"
  | "members"
  | "processes"
  | "programs"
  | "projects"
  | "requests"
  | "skills"
  | "templates"
  | "time_off"
  | "work_items";

/**
 * What `export_created` reports for one list export (NG-2062). Required, so a
 * new list export can't ship without naming what it exports.
 */
interface CsvExportAnalytics {
  exportType: CsvExportType;
  /**
   * The rows picked in the table. Selected rows are a bulk-bar action (`source:
   * bulk`, with a `count`). None, or an empty list, exports the whole filtered
   * list from the page.
   */
  selectedIds?: readonly string[];
}

function trackCsvExport(analytics: CsvExportAnalytics, mode: "sync" | "async"): void {
  const count = analytics.selectedIds?.length ?? 0;
  track("export_created", {
    export_type: analytics.exportType,
    format: "csv",
    mode,
    ...(count > 0 ? { count, source: "bulk" } : { source: "page" }),
  });
}

/**
 * Branch a list-export Response: 200 CSV → download; 202 → queue + poll.
 *
 * `export_created` fires once per export, when the server has made it: on the
 * download (`mode: sync`), or once the export is queued (`mode: async`). A
 * failed request fires nothing. The poll opening the finished file is the same
 * export, so it fires nothing either.
 */
export async function runCsvExport(
  request: () => Promise<Response>,
  handlers: CsvExportHandlers,
  analytics: CsvExportAnalytics,
): Promise<void> {
  const resp = await request();
  if (resp.status === 202) {
    const { token } = (await resp.json()) as { token: string };
    handlers.onQueued(token);
    trackCsvExport(analytics, "async");
    const url = await handlers.poll(token);
    if (url) window.open(url, "_blank", "noopener");
    return;
  }
  if (!resp.ok) {
    throw new Error(`Export failed with status ${resp.status}`);
  }
  const blob = await resp.blob();
  const disposition = resp.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  handlers.download(blob, match?.[1] ?? "export.csv");
  trackCsvExport(analytics, "sync");
}

const NON_EXPORTABLE = new Set(["check", "actions"]);

/**
 * Compute the ordered list of visible, exportable column IDs from the table's
 * column preferences. Strips UI-only columns (check/actions) and hidden
 * columns, preserving the user's chosen order.
 */
export function visibleExportColumns(
  columnOrder: string[],
  columnVisibility: Record<string, boolean>,
): string[] {
  return columnOrder.filter((id) => !NON_EXPORTABLE.has(id) && columnVisibility[id] !== false);
}

/** Default browser download via a temporary object URL. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export interface PollExportOptions {
  /** Delay between polls, ms. */
  intervalMs?: number;
  /** Give up after this many polls. */
  maxAttempts?: number;
  /** Injectable sleep (tests pass an immediate resolver). */
  wait?: (ms: number) => Promise<void>;
}

/**
 * Poll `fetchStatus(token)` until the export completes (a `url` appears),
 * fails, or attempts run out. Returns the presigned URL or null. Shared by the
 * Requests and Projects stores so polling semantics stay identical.
 */
export async function pollExportStatus(
  token: string,
  fetchStatus: (token: string) => Promise<ExportStatus>,
  { intervalMs = 2000, maxAttempts = 150, wait }: PollExportOptions = {},
): Promise<string | null> {
  const delay = wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  // api-demand: follows one export job until it finishes, fails or the attempts run out (NG-2493)
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const status = await fetchStatus(token);
    if (status.url) return status.url;
    if (status.status === "failed") return null;
    await delay(intervalMs);
  }
  return null;
}
